import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import {
  commissionsApi,
  type RateGridCellDto,
  type RateGridPoolDto,
  type RateGridRowDto,
} from "@/api/commissions";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Table, TBody, TD, TH, THead, TR } from "@/design-system/components/ui";

export function CommissionRatesPage() {
  const grid = useQuery({
    queryKey: commissionKeys.rateGrid(),
    queryFn: () => commissionsApi.rateGrid(),
  });

  return (
    <div className="flex flex-col gap-space-5">
      <div>
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
          Commissions · Admin
        </div>
        <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
          Commission rates
        </h1>
        <p className="text-body text-gray-mid mt-space-2 max-w-reading">
          Per-pool rates for each commissionable position a staffer holds. Someone in two positions
          has a row for each, at that position&apos;s default. The default (from the position&apos;s
          allocation) is shown as a placeholder; type a value to override it for this person in that
          pool, or clear it to inherit the default again. An override covers every position the
          person holds in that pool, so it shows on each of their rows.
        </p>
      </div>

      {grid.isLoading ? (
        <div className="font-display text-label uppercase tracking-label text-gray-mid">Loading…</div>
      ) : grid.data && grid.data.rows.length > 0 ? (
        <Table>
          <THead>
            <TR>
              <TH>Staff</TH>
              {grid.data.pools.map((p) => (
                <TH key={p.poolId} className="text-right">
                  {p.code}
                </TH>
              ))}
            </TR>
          </THead>
          <TBody>
            {grid.data.rows.map((row) => (
              <TR key={`${row.userId}:${row.roleCode}`}>
                <TD>
                  <div className="font-display font-bold text-onyx">{row.displayName ?? row.userId}</div>
                  <div className="text-caption text-gray-mid">
                    {row.roleCode} · {row.roleTitle}
                  </div>
                </TD>
                {grid.data!.pools.map((p) => {
                  const cell = row.cells.find((c) => c.poolId === p.poolId);
                  return (
                    <TD key={p.poolId} className="text-right">
                      {/* Keyed on the override so an edit made on this person's
                          other row re-seeds this input's draft after the refetch. */}
                      <RateCell key={String(cell?.override ?? "")} row={row} pool={p} cell={cell} />
                    </TD>
                  );
                })}
              </TR>
            ))}
          </TBody>
        </Table>
      ) : (
        <div className="text-body text-gray-mid">No staff hold a commissionable position yet.</div>
      )}
    </div>
  );
}

function RateCell({
  row,
  pool,
  cell,
}: {
  row: RateGridRowDto;
  pool: RateGridPoolDto;
  cell: RateGridCellDto | undefined;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string>(cell?.override != null ? String(cell.override) : "");

  const invalidate = () => qc.invalidateQueries({ queryKey: commissionKeys.rateGrid() });

  const setOverride = useMutation({
    mutationFn: (percent: number) =>
      commissionsApi.setRateOverride({ userId: row.userId, revenuePoolId: pool.poolId, percent }),
    onSuccess: invalidate,
  });
  const clearOverride = useMutation({
    mutationFn: () =>
      commissionsApi.clearRateOverride({ userId: row.userId, revenuePoolId: pool.poolId }),
    onSuccess: invalidate,
  });

  const hasOverride = cell?.override != null;
  const defaultLabel = cell?.default != null ? cell.default.toFixed(2) : "—";

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === "") {
      if (hasOverride) clearOverride.mutate();
      return;
    }
    const value = Number(trimmed);
    if (Number.isNaN(value) || value < 0 || value > 100) return;
    if (value !== cell?.override) setOverride.mutate(value);
  };

  return (
    <div className="flex items-center justify-end gap-space-1">
      <input
        type="number"
        step="0.01"
        min={0}
        max={100}
        value={draft}
        placeholder={defaultLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        className={`w-16 px-space-2 py-space-1 text-right tabular-nums border rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none ${
          hasOverride ? "border-gold text-onyx" : "border-border-light text-gray-mid"
        }`}
        aria-label={`${row.roleCode} rate for pool ${pool.code}`}
      />
      {hasOverride && (
        <button
          type="button"
          onClick={() => {
            setDraft("");
            clearOverride.mutate();
          }}
          className="p-space-1 rounded-full text-gray-mid hover:text-status-error"
          aria-label="Clear override (inherit default)"
        >
          <X size={12} strokeWidth={2} aria-hidden />
        </button>
      )}
    </div>
  );
}
